import React, {useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {createPortal} from 'react-dom'
import RotatePhoneIcon from './rotate_phone_icon'
import useStickyToastPosition from '../hooks/useStickyToastPosition'
import {getTopPortalTarget} from '../utils/portal'
import {TOAST_LINK_COPIED, TOAST_LINK_COPY_FAILED, TOAST_ROTATE_HINT} from '../actions/toast_index'
import { componentText as t } from '../utils/i18n_utils'

const CheckIcon = () => <i className="fa-regular fa-check" aria-hidden="true"/>

const ErrorIcon = () => <i className="fa-regular fa-circle-exclamation" aria-hidden="true"/>

// Confirmations fade on their own, guidance waits to be closed: only the
// rotate hint is dismissible, and only it counts toward the coach-mark budget.
const TOAST_KINDS = {
  [TOAST_LINK_COPIED]: {
    icon: <CheckIcon/>,
    iconModifier: 'lbv2__toast-icon--success',
    autoHideMs: 4000,
    dismissible: false,
    message: () => t('share_button.link_copied', 'Link copied to clipboard'),
  },
  [TOAST_LINK_COPY_FAILED]: {
    icon: <ErrorIcon/>,
    iconModifier: 'lbv2__toast-icon--error',
    autoHideMs: 4000,
    dismissible: false,
    message: () => t('share_button.link_copy_failed', 'The link could not be copied. Please try again.'),
  },
  [TOAST_ROTATE_HINT]: {
    icon: <RotatePhoneIcon size={27}/>,
    iconModifier: 'lbv2__toast-icon--hint',
    autoHideMs: null,
    dismissible: true,
    message: () =>
      t('orientation_button.rotate_hint', 'Rotate your device to landscape for the full leaderboard'),
  },
}

// Dark slate toast pinned to the top right of the leaderboard (Figma node
// 20672:257050). onDismiss is the X only — the coach mark counts it, and a stray
// tap elsewhere must not retire guidance the user has not read yet. Auto-hide
// goes through onHide instead, so it never counts as a dismissal.
//
// Rendered into the top document, like the module's modals: an embedded
// leaderboard sits in an iframe the wrapper keeps at content height, so a toast
// left inside the frame scrolls out of reach on the parent page.
const Lbv2Toast = ({kind, nonce, onHide, onDismiss}) => {
  const config = TOAST_KINDS[kind]
  const dismissible = !!config && config.dismissible
  const portalTarget = useMemo(() => getTopPortalTarget(), [])
  const toastRef = useRef(null)
  const {top, right} = useStickyToastPosition(!!config, toastRef)

  // nonce is a dep so re-showing the kind already on screen restarts the
  // countdown - without it the second toast would vanish on the first one's
  // timer.
  useEffect(() => {
    if (!config || !config.autoHideMs) {
      return undefined
    }

    const timerId = setTimeout(onHide, config.autoHideMs)
    return () => clearTimeout(timerId)
  }, [ config, nonce, onHide ])

  if (!portalTarget) {
    return null
  }

  // The role="status" region stays mounted (empty) even with no toast: screen
  // readers only announce changes to a live region that already exists in the
  // accessibility tree, so a region inserted with its text already inside is
  // unreliably (often never) read. The empty wrapper is unstyled and sizeless;
  // the toast box is position: fixed, so the extra parent changes no layout.
  return createPortal(
    <div role="status">
      {config && (
        // Keyed by nonce: a repeat of the same kind remounts the content, and
        // that mutation inside the persistent live region is what makes screen
        // readers announce the repeat. No visual effect - the box has no entry
        // animation and its position lives in the hook.
        <div className="lbv2__toast" key={nonce} ref={toastRef} style={{top: `${top}px`, right: `${right}px`}}>
          <span className={`lbv2__toast-icon ${config.iconModifier}`}>{config.icon}</span>
          <span className="lbv2__toast-message">{config.message()}</span>
          {dismissible && (
            <button
              type="button"
              className="lbv2__toast-close"
              aria-label={t('toast.dismiss', 'Dismiss')}
              onClick={onDismiss}>
              <i className="fa-regular fa-xmark" aria-hidden="true"/>
            </button>
          )}
        </div>
      )}
    </div>,
    portalTarget
  )
}

Lbv2Toast.propTypes = {
  kind: PropTypes.string,
  nonce: PropTypes.number,
  onHide: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
}

export default Lbv2Toast
